/**
 * Formats a number to a compact string representation (e.g., 1.2k, 1.5M)
 */
export function formatCompactNumber(number: number): string {
  return Intl.NumberFormat('en-US', {
    notation: 'compact',
    maximumFractionDigits: 1,
  })
    .format(number)
    .toLowerCase();
}

/**
 * A line count in at most four characters: as is below 1,000, then "7.8k", "12k" and "1.2M".
 * Each band starts where the one below would round up to its next unit.
 */
export function formatCompactLineCount(count: number): string {
  if (count < 1000) {
    return String(count);
  }
  if (count < 9950) {
    return `${(count / 1000).toFixed(1)}k`;
  }
  if (count < 999_500) {
    return `${Math.round(count / 1000)}k`;
  }
  return `${(count / 1_000_000).toFixed(1)}M`;
}

/**
 * Formats token counts using uppercase K/M suffixes to match quota and usage UI.
 */
export function formatTokens(number: number): string {
  if (number >= 1_000_000) {
    return `${(number / 1_000_000).toFixed(number % 1_000_000 === 0 ? 0 : 1)}M`;
  }
  if (number >= 1_000) {
    return `${(number / 1_000).toFixed(number % 1_000 === 0 ? 0 : 1)}K`;
  }
  return number.toLocaleString();
}

export function formatDollarsFromCents(
  cents: number,
  options: Intl.NumberFormatOptions = {},
): string {
  return `$${(cents / 100).toLocaleString('en-US', options)}`;
}

/**
 * An amount in its currency's smallest unit, as Stripe reports it, in that currency: "$40.00",
 * or "¥500" for yen, which has no smaller unit. US formatting, like the app's other amounts.
 */
export function formatStripeAmount(value: { amount: number; currency: string }): string {
  const formatter = new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: value.currency,
  });
  return formatter.format(
    value.amount / 10 ** (formatter.resolvedOptions().maximumFractionDigits ?? 2),
  );
}

/** "$250", or "$40.50" when there are cents. */
export function formatMoney(cents: number): string {
  const fractionDigits = cents % 100 === 0 ? 0 : 2;
  return formatDollarsFromCents(cents, {
    minimumFractionDigits: fractionDigits,
    maximumFractionDigits: fractionDigits,
  });
}

const usdFormatter = new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' });

/**
 * Formats US dollars to the cent. A positive amount under one cent reads `<$0.01`, so it is
 * never mistaken for nothing.
 */
export function formatCostUsd(amount: number): string {
  if (amount > 0 && amount < 0.01) {
    return '<$0.01';
  }
  return usdFormatter.format(amount);
}
